import { Plugin, PluginKey } from "prosemirror-state";
import { DOMParser as ProseMirrorDOMParser } from 'prosemirror-model';
import { Extension } from "@tiptap/core";
import Swal from "sweetalert2";

import { serializeForClipboard } from "../utils/clipboard";
import { isEmptySelection } from "../utils/selection";
import { colors } from "../../../collections/colors";

// Standard ProseMirror parseHTML function
function parseHTML(html) {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div;
}

const brokenClipboardAPI = !(navigator.userAgent.indexOf("Chrome") != -1); // (browser.ie && browser.ie_version < 15) || (browser.ios && browser.webkit_version < 604)

if (typeof window.requestIdleCallback !== "function") {
  window.requestIdleCallback = function (cb) {
    return setTimeout(() => {
      cb({
        didTimeout: false,
        timeRemaining: () => Math.max(0, 50),
      });
    }, 1);
  };
}

if (typeof window.cancelIdleCallback !== "function") {
  window.cancelIdleCallback = function(id) {
    clearTimeout(id);
  };
}

const nameToBlocks = {
  circle: "space",
  grid1: "grid",
  grid2: "grid",
  grid3: "grid",
  grid4: "grid",
  column: "column",
  resource: "collapsable section",
  survey: "survey",
  interactiveInput: "interactive input",
  tagsFeed: "tag feed",
  infobox: "info box",
};

export function getNode(node, coords) {
  try {
    node = node.node;

    while (node && node.parentNode) {
      if (
        (node.parentNode.classList &&
          node.parentNode.classList.contains("ProseMirror")) ||
        (node.dataset && node.dataset.uid)
      ) {
        // todo
        break;
      }
      node = node.parentNode;
    }
  } catch (e) {}
  return node;
}

export function blockPosAtCoords(coords, view) {
  let pos = view.posAtCoords(coords);
  if (!pos) {
    return null;
  }
  let node = view.domAtPos(pos.pos);

  node = getNode(node, coords);

  if (node && node.nodeType === 1) {
    let desc = view.docView.nearestDesc(node, true);
    if (!(!desc || desc === view.docView)) {
      return desc.posBefore;
    }
  }
  return null;
}

export function blockAtPos(pos, view) {
  let node = view.domAtPos(pos);

  node = getNode(node, view.coordsAtPos(pos));

  if (node && node.nodeType === 1) {
    let desc = view.docView.nearestDesc(node, true);
    if (!(!desc || desc === view.docView)) {
      return desc.posBefore;
    }
  }
  return null;
}

export function onDragStart(e, pos, editor) {
  if (!e.dataTransfer) return;

  e.preventDefault();
  e.stopPropagation();

  pos = editor.view.state.selection.from;

  let slice = editor.view.state.selection.content();
  let node = editor.view.state.selection.node;

  if (!node && slice.content.content.length == 0) {
    editor.commands.selectTextblockStart();
    pos = editor.view.state.selection.from;
    let selectBack = false;
    try {
      
      if (pos > 0) {
        editor.commands.setNodeSelection(pos - 1);
        selectBack = true;
      }
    } catch (e) {
      editor.commands.setNodeSelection(pos);

      const auxSelection = editor.view.state.selection;
      if (
        auxSelection.$anchor.depth > 0 &&
        auxSelection.$anchor.path[3].type.name == "heading"
      ) {
        editor.commands.selectParentNode();
      }
      selectBack = false;
    }

    node = editor.view.state.selection.node;

    if (node && node.type.name == "text") {
      editor.commands.selectParentNode();
    }

    try {
      if (pos > 1) {
        let auxNode = editor.view.state.doc.nodeAt(pos - 2);

        if (
          auxNode &&
          (auxNode.type.name == "listItem" || auxNode.type.name == "taskItem")
        ) {
          editor.commands.setNodeSelection(pos - 2);
        }
      }
    } catch (e) {
    } finally {
    }

    slice = editor.view.state.selection.content();
  }

  let { dom, text } = serializeForClipboard(editor.view, slice);
  console.log(dom);

  // e.dataTransfer.clearData()
  try {
    e.dataTransfer.effectAllowed = "all";

    e.dataTransfer.setData(
      brokenClipboardAPI ? "text" : "text/html",
      dom.innerHTML
    );
    if (!brokenClipboardAPI)
      e.dataTransfer.setData("text/plain", dom.innerText);

    editor.view.dragging = { slice, move: true };

    return slice;
  } catch (e) {
  } finally {
  }

  // }
}

export function onDragClick(e, view) {
  let coords = { left: e.clientX + 50, top: e.clientY };
  let pos = view.posAtCoords(coords);
  let node = view.domAtPos(pos.pos);

  node = getNode(node, coords);
  view.dom.editor.commands.focus(pos.pos);

  if (
    !view.state.selection.$anchor.parent.isTextblock &&
    view.state.selection.$anchor.parent.type.name != "circle"
  ) {
    view.dom.editor.commands.focus(
      view.state.selection.$anchor.pos +
        view.state.selection.$anchor.parent.content.size
    );
  }
}

// Helper function to calculate data size in bytes
function calculateDataSize(data) {
  if (!data) return 0;
  return new Blob([data]).size;
}

// Helper function to check if data size is large (20KB or more)
function isLargeData(data) {
  const sizeInBytes = calculateDataSize(data);
  const sizeInKB = sizeInBytes / 1024;
  return sizeInKB >= 20;
}

function handlePaste(view, event) {
  const { clipboardData } = event;
  
  // 1. Get clipboard data
  const text = clipboardData.getData("text/plain");
  const html = clipboardData.getData("text/html");

  if (typeof text === "string" && text.length > 0) return false;
  
  if (typeof html === "string" && html.length > 0) {
    // 2. Check if data is large enough to show loading modal
    const isLarge = isLargeData(html) || isLargeData(text);
  
    // 3. Show loading modal only for large data
    if (isLarge) {
      Swal.fire({
        title: "Processing content...",
        text: "Please wait while we process your content",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
    }
    
    // 4. Parse HTML through ProseMirror
    const parser = ProseMirrorDOMParser.fromSchema(view.state.schema);
    const dom = parseHTML(html);
    const slice = parser.parseSlice(dom);
    
    insertSliceSmoothly(view, slice);
    return true;
  }

  // 5. Fallback to plain text
  if (text) {
    const tr = view.state.tr.replaceSelection(
      view.state.schema.text(text)
    );
    view.dispatch(tr);
    return true;
  }
  
  return false;
}

// Smooth slice insertion without blocking DOM
function insertSliceSmoothly(view, slice) {
  const { content } = slice;
  const { state } = view;
  let currentPos = state.selection.from;
  
  // Ensure starting position is within bounds
  const docSize = state.doc.content.size;
  if (currentPos > docSize) {
    currentPos = docSize;
  }
  
  let tr = state.tr;
  let index = 0;
  
  function insertNextNode() {
    if (index >= content.childCount) {
      // All nodes inserted, close modal
      Swal.close();
      return;
    }
    
    // Create fresh transaction from current state
    tr = view.state.tr;
    
    const node = content.child(index);
    
    // Check if position is within document bounds
    const docSize = view.state.doc.content.size;
    if (currentPos > docSize) {
      console.log(`Position ${currentPos} out of range, resetting to ${docSize}`);
      currentPos = docSize;
    }
    
    // Simple validation - check if node type exists in schema
    try {
      const schema = view.state.schema;
      const nodeType = schema.nodes[node.type.name];
      
      if (!nodeType) {
        console.log(`Unknown node type ${node.type.name}, skipping`);
        index++;
        return;
      }
      
      // Special handling for column nodes - extract content and insert it
      if (node.type.name === 'column') {
        // Extract content from column and insert it directly
        if (node.content && node.content.content) {
          node.content.forEach(childNode => {
            tr = tr.insert(currentPos, childNode);
            currentPos += childNode.nodeSize;
          });
        }
      } else {
        // Insert node at current position for all other types
        tr = tr.insert(currentPos, node);
      }
    } catch (error) {
      console.log(`Error inserting node ${node.type.name}:`, error);
      index++;
      return;
    }
    
    // Update position for next insertion
    if (node.type.name === 'horizontalRule') {
      currentPos += node.nodeSize + 1; // Extra space for horizontalRule
    } else {
      currentPos += node.nodeSize;
    }
    index++;

    // Dispatch transaction and get fresh state
    view.dispatch(tr);
    
    // Schedule next node insertion with additional delay for smooth rendering
    requestIdleCallback(() => {
      setTimeout(() => {
        insertNextNode();
      }, 10); // Additional 10ms delay for smooth rendering
    }, { timeout: 50 });
  }
  
  // Error handling - close modal if something goes wrong
  try {
    insertNextNode();
  } catch (error) {
    console.error("Error during slice insertion:", error);
    Swal.close();
  }
  
  // Start the smooth insertion
  insertNextNode();
}

const EventHandler = Extension.create({
  name: "eventHandler",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("eventHandler"),

        props: {
          handleDOMEvents: {
            copy: (view, event) => {
              
              if (brokenClipboardAPI) {
                console.log("brokenClipboardAPI");
                return;
              }
              event.preventDefault();
              const slice = view.state.selection.content();

              console.log(slice);
              let { dom, text } = serializeForClipboard(view, slice);
              console.log(dom);
              console.log("COPYING");
              console.log(dom.innerHTML);

              event.clipboardData.clearData();
              try {
                event.clipboardData.effectAllowed = "all";

                event.clipboardData.setData(
                  brokenClipboardAPI ? "text" : "text/html",
                  dom.innerHTML
                );

                if (!brokenClipboardAPI)
                  event.clipboardData.setData("text/plain", dom.innerText);

                view.dragging = { slice, move: true };

                return slice;
              } catch (e) {
                console.log(e);
              } finally {
              }
            },
            cut: (view, event) => {
              
              const dontDelete = [
                "grid1",
                "grid2",
                "grid3",
                "grid4",
                "column",
                "resource",
                "circle",
                "survey",
                "interactiveInput",
                "tagsFeed",
                "infobox",
                "orderedList",
                "bulletList",
                "listItem",
              ];

              const { state, dispatch } = view;
              const { selection } = state;
              const { $from, $to } = selection;
              let tr = state.tr;

              const hasListItem = (() => {
                let valid = false;
                state.doc.nodesBetween($from.pos, $to.pos, (node) => {
                  const name = node.type.name;
                  if (["orderedList", "bulletList", "listItem"].includes(name)) {
                    valid = true;
                    return;
                  }
            
                  return false;
                });
                return valid;
              })();

              if (hasListItem) {
                event.preventDefault();
            
                const slice = state.selection.content();
                const { dom } = serializeForClipboard(view, slice);
            
                try {
                  event.clipboardData?.setData("text/html", dom.innerHTML);
                  event.clipboardData?.setData("text/plain", dom.innerText);
                } catch (e) {
                  console.warn("Clipboard copy error:", e);
                }
            
                return view.dom.editor.commands.deleteSelection();
              }
  
              // Check if the selection is not empty
              let positionsToDelete = [];
              if (!$from.sameParent($to)) {
                state.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
                  if (node.isBlock && dontDelete.indexOf(node.type.name) < 0) {
                    let startPos = pos;
                    let endPos = pos + node.nodeSize;
  
                    if ($from.pos > startPos && $from.pos < endPos) {
                      startPos = $from.pos;
                    }
  
                    if ($to.pos > startPos && $to.pos < endPos) {
                      endPos = $to.pos;
                    }
                    positionsToDelete.push({
                      start: startPos,
                      end: endPos,
                      name: node.type.name,
                    });
                  }
                });
  
                console.log(positionsToDelete);
                let changed = false;
                for (let i = positionsToDelete.length - 1; i >= 0; i--) {
                  try{
                    const { start, end, name } = positionsToDelete[i];
                    tr = tr.delete(start, end);
                    changed = true;
                  }
                  catch{

                  }
                }
  
                if (changed) {
                  event.preventDefault();
                  document.execCommand("copy");
                  view.dispatch(tr);
                  return true;
                }
                return false;
              } else {
                // Handle single node cut
                try {
                  const posBefore = $from.pos + 1;
                  const posBefore2 = $from.pos + 2;
                  const currentNode = state.doc.nodeAt($from.pos);
                  const nodeBefore = state.doc.nodeAt(posBefore);
                  const nodeBefore2 = state.doc.nodeAt(posBefore2);
                  let nodeToDelete = null;
                  let nodeToDeletePos = null;
  
                  if (
                    currentNode &&
                    dontDelete.indexOf(currentNode.type.name) >= 0 && currentNode.type.name != "listItem"
                  ) {
                    nodeToDelete = currentNode;
                    nodeToDeletePos = $from.pos;
                  } else if (
                    nodeBefore &&
                    dontDelete.indexOf(nodeBefore.type.name) >= 0 && nodeBefore.type.name != "listItem"
                  ) {
                    nodeToDelete = nodeBefore;
                    nodeToDeletePos = posBefore;
                  } else if (
                    nodeBefore2 &&
                    dontDelete.indexOf(nodeBefore2.type.name) >= 0 && nodeBefore2.type.name != "listItem"
                  ) {
                    nodeToDelete = nodeBefore2;
                    nodeToDeletePos = posBefore2;
                  }
  
                  if (nodeToDelete) {
                    event.preventDefault();
                    Swal.fire({
                      title: "Are you sure?",
                      html: `You are about to cut a content block.`,
                      showDenyButton: true,
                      confirmButtonText: "Yes, cut",
                      denyButtonText: "No",
                      confirmButtonColor: colors.red,
                      denyButtonColor: colors.gray,
                    }).then((result) => {
                      if (result.isConfirmed) {
                        document.execCommand("copy");
                        tr = tr.delete(nodeToDeletePos, nodeToDeletePos + 1);
                        view.dispatch(tr);
                      } else if (result.isDenied) {
                        // If denied, do nothing
                      }
                    });
                    return true;
                  }
                } catch (e) {
                  console.error("Error handling cut event:", e);
                }
              }
              return false;
            }
          },
          handleKeyDown: (view, event) => {
            const dontDelete = [
              "grid1",
              "grid2",
              "grid3",
              "grid4",
              "column",
              "resource",
              "circle",
              "survey",
              "interactiveInput",
              "interactiveButton",
              "interactiveWait",
              "tagsFeed",
              "infobox",
              "orderedList",
              "bulletList",
              "listItem",
              "video",
              "image"
            ];

            const normalEnter = [
              "grid1",
              "grid2",
              "grid3",
              "grid4",
              "column",
              "resource",
              "circle",
              "survey",
              "interactiveInput",
              "interactiveButton",
              "interactiveWait",
              "tagsFeed",
              "infobox",
              "orderedList",
              "bulletList",
              "listItem",
              "video", 
              "image", 
              "file", 
              "audio", 
              "embed", 
              "actionbutton", 
              "customform", 
            ];

            const { selection } = view.state;
            const { $anchor, empty } = selection;

            if (event.key == "Enter") {
              // event.preventDefault();

              if (
                !(
                  selection.$anchor.depth > 1 &&
                  selection.$anchor.path[3] &&
                  selection.$anchor.path[3].type.name.indexOf("List")
                )
              ) {
                if (!isEmptySelection(view.dom.editor)) {
                  let node = view.state.doc.nodeAt(selection.$anchor.pos);
                  if (!node){
                    node = selection.$anchor.parent;
                  }
                  
                  if (normalEnter.indexOf(node.type.name) >= 0){
                    return false;
                  }
                  view.dom.editor.commands.customEnter();
                  return true;
                }
              }
              // return true;
            }

            if (
              selection.$anchor.depth > 1 &&
              selection.$anchor.path[3] &&
              selection.$anchor.path[3].type.name.indexOf("List") >= 0
            ) {
              return false;
            }

            if (event.key == "Backspace") {
              const { state, dispatch } = this.editor;
              const { selection } = state;
              const { $from, $to } = selection;
              let tr = state.tr;

              // Find the parent block that should not be deleted
              const parentBlock = $from.node($from.depth);
              const parentBlockStart = $from.start($from.depth);
              const parentBlockEnd = $from.end($from.depth);

              // Check if the entire block is selected (e.g., the whole column)
              const isWholeBlockSelected = (
                $from.pos === parentBlockStart &&
                $to.pos === parentBlockEnd
              );

              if (
                isWholeBlockSelected &&
                dontDelete.includes(parentBlock.type.name) &&
                parentBlock.type.name !== "listItem"
              ) {
                Swal.fire({
                  title: "Are you sure?",
                  html: `You are about to delete a content block.`,
                  showDenyButton: true,
                  confirmButtonText: "Yes, delete",
                  denyButtonText: "No",
                  confirmButtonColor: colors.red,
                  denyButtonColor: colors.gray,
                }).then((result) => {
                  if (result.isConfirmed) {
                    tr = tr.delete(parentBlockStart - 1, parentBlockEnd + 1); // delete the block
                    this.editor.view.dispatch(tr);
                  }
                });
                return true;
              }

              // If not the whole block is selected — perform normal content deletion
              return false;
            }
            if (event.key == "Delete") {
              const { state, dispatch } = this.editor;
              const { selection } = state;
              const { $from, $to } = selection;
              let tr = state.tr;

              // Check if the selection is not empty
              let positionsToKeep = [];
              let positionsToDelete = [];
              if (!$from.sameParent($to)) {
                state.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
                  // Check if the node is a 'circle' node
                  if (node.isBlock) {
                    if (dontDelete.indexOf(node.type.name) < 0) {
                      let startPos = pos;
                      let endPos = pos + node.nodeSize;

                      if ($from.pos > startPos && $from.pos < endPos) {
                        startPos = $from.pos;
                      }

                      if ($to.pos > startPos && $to.pos < endPos) {
                        endPos = $to.pos;
                      }
                      positionsToDelete.push({
                        start: startPos,
                        end: endPos,
                        name: node.type.name,
                      });
                    }
                  }
                });
                console.log(positionsToKeep);
                console.log(positionsToDelete);
                let changed = false;
                for (let i = positionsToDelete.length - 1; i >= 0; i--) {
                  try{
                    const { start, end, name } = positionsToDelete[i];
                    tr = tr.delete(start, end);
                    changed = true;
                  }
                  catch{

                  }
                }

                if (changed) {
                  this.editor.view.dispatch(tr);
                  return true;
                }
                return true;
              } else {
                // Handle single backspace (single character or node deletion)
                try {
                  const posBefore = $from.pos + 1;
                  const posBefore2 = $from.pos + 2;
                  const currentNode = state.doc.nodeAt($from.pos);
                  const nodeBefore = state.doc.nodeAt(posBefore);
                  const nodeBefore2 = state.doc.nodeAt(posBefore2);
                  let nodeToDelete = null;
                  let nodeToDeletePos = null;
                  if (
                    currentNode &&
                    dontDelete.indexOf(currentNode.type.name) >= 0 && currentNode.type.name != "listItem"
                  ) {
                    nodeToDelete = currentNode;
                    nodeToDeletePos = $from.pos;
                  } else if (
                    nodeBefore &&
                    dontDelete.indexOf(nodeBefore.type.name) >= 0 && nodeBefore.type.name != "listItem"
                  ) {
                    nodeToDelete = nodeBefore;
                    nodeToDeletePos = posBefore;
                  } else if (
                    nodeBefore2 &&
                    dontDelete.indexOf(nodeBefore2.type.name) >= 0 && nodeBefore2.type.name != "listItem"
                  ) {
                    nodeToDelete = nodeBefore2;
                    nodeToDeletePos = posBefore2;
                  }

                  if (
                    (currentNode &&
                      dontDelete.indexOf(currentNode.type.name) >= 0) ||
                    (nodeBefore &&
                      dontDelete.indexOf(nodeBefore.type.name) >= 0) ||
                    (nodeBefore2 &&
                      dontDelete.indexOf(nodeBefore2.type.name) >= 0)
                  ) {
                    Swal.fire({
                      title: "Are you sure?",
                      html: `You are about to delete a ${nodeToDelete.type.name} block.`,
                      showDenyButton: true,
                      confirmButtonText: "Yes, delete",
                      denyButtonText: "No",
                      confirmButtonColor: colors.red,
                      denyButtonColor: colors.gray,
                    }).then((result) => {
                      if (result.isConfirmed) {
                        console.log(nodeToDelete);
                        console.log(nodeToDeletePos);
                        tr = tr.delete(nodeToDeletePos, nodeToDeletePos + 1);
                        this.editor.view.dispatch(tr);
                      } else if (result.isDenied) {
                      }
                    });
                    return true;
                  } else {
                    // Default backspace behavior for other nodes or text
                    return false;
                  }
                } catch {}
              }
              return false;
            }
          },
          handleKeyPress: (view, event) => {
            const { selection } = view.state;
            const { $anchor, empty } = selection;

            // if (event.key == "Enter") {
            //   event.preventDefault();
            //   view.dom.editor.commands.customEnter()
            //   return true;
            // }
          },
          handlePaste: (view, event) => handlePaste(view, event),
          // handlePaste(view, event, slice) {
          //   const { clipboardData } = event;
            
          //   // 1. Get clipboard data
          //   const text = clipboardData.getData("text/plain");
          //   const html = clipboardData.getData("text/html");

          //   console.log("HTML::: ", html);
            
          //   if (!text && !html) return false;
            
          //   // 2. Prevent multiple paste operations
          //   if (window.$isPasteInProgress) {
          //     event.preventDefault();
          //     return true;
          //   }
            
          //   // 3. Show loading modal immediately
          //   window.$isPasteInProgress = true;
            
          //   Swal.fire({
          //     title: "Processing content...",
          //     text: "Please wait while we process your content",
          //     allowOutsideClick: false,
          //     allowEscapeKey: false,
          //     showConfirmButton: false,
          //     didOpen: () => {
          //       Swal.showLoading();
          //     }
          //   });
            
          //   // 4. Parse HTML to AST blocks using our simple parser
          //   const processPaste = () => {
          //     try {
          //       // Validate view and state
          //       if (!view || !view.state) {
          //         console.error("Invalid view or state");
          //         Swal.close();
          //         window.$isPasteInProgress = false;
          //         return;
          //       }
                
          //       let blocks = [];
                
          //       if (html) {
          //         // Use our simple parser
          //         blocks = parseHtmlToAst(html, view.state.schema);
          //       }

          //       console.log("blocks", blocks);
          //       console.log("Blocks to insert in order:", blocks.map((block, i) => `${i}: ${block.type.name}`));
                
          //       // 5. Insert all blocks in a single transaction with correct positions
          //       if (blocks.length > 0) {
          //         try {
          //           console.log("Inserting all blocks in single transaction");
                    
          //           // Create a single transaction
          //           let tr = view.state.tr;
          //           let currentPos = tr.selection.from;
                    
          //           // Insert all blocks sequentially
          //           for (let i = 0; i < blocks.length; i++) {
          //             const block = blocks[i];
          //             console.log(`Adding block ${i} (${block.type.name}) at position ${currentPos}`);
                      
          //             // Insert block at current position
          //             tr = tr.insert(currentPos, block);
                      
          //             // Calculate next position
          //             currentPos += block.nodeSize;
          //           }
                    
          //           // Dispatch the single transaction
          //           view.dispatch(tr);
                    
          //           console.log("All blocks inserted successfully");
                    
          //           // Close modal
          //           Swal.close();
          //           window.$isPasteInProgress = false;
                    
          //         } catch (error) {
          //           console.error("Error inserting blocks:", error);
                    
          //           // Fallback: insert as plain text
          //           if (text) {
          //             const tr = view.state.tr.replaceSelection(
          //               view.state.schema.text(text)
          //             );
          //             view.dispatch(tr);
          //           }
                    
          //           // Close modal on error
          //           Swal.close();
          //           window.$isPasteInProgress = false;
          //         }
                  
          //       } else if (text) {
          //         // Fallback to plain text
          //         const tr = view.state.tr.replaceSelection(
          //           view.state.schema.text(text)
          //         );
          //         view.dispatch(tr);
                  
          //         // Close modal after text insertion
          //         console.log("Closing Swal modal...");
          //         Swal.close();
          //         window.$isPasteInProgress = false;
          //       }
                
          //     } catch (error) {
          //       console.error("Error processing paste:", error);
          //       // Fallback to plain text
          //       if (text) {
          //         const tr = view.state.tr.replaceSelection(
          //           view.state.schema.text(text)
          //         );
          //         view.dispatch(tr);
          //       }
                
          //       // Close modal on error
          //       console.log("Closing Swal modal...");
          //       Swal.close();
          //       window.$isPasteInProgress = false;
          //     }
          //   };
            
          //   // Start processing
          //   processPaste();
            
          //   return true;
          // }
        },
      }),
    ];
  },
});





export default EventHandler;
